import { useCallback, useId, useState } from 'react';
import type * as React from 'react';
import { useCreate, useDelete, useUpdate } from '@refinedev/core';
import { ChevronRight, Plus, Trash2 } from 'lucide-react';
import { Page, Notice } from '@/components/page';
import { EmptyState, ErrorState, TableSkeleton } from '@/components/states';
import { SortableList } from '@/components/sortable-list';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogForm,
  DialogBody,
  DialogContent,
  DialogFooter,
} from '@/components/ui/dialog';
import { FormField, Input } from '@/components/ui/field';
import { Badge, Mono, Switch, Tooltip } from '@/components/ui/primitives';
import { RESOURCES, type AdminOption, type AdminOptionSet } from '@/api/resources';
import { useAdminList, useReorder } from '@/hooks/use-admin-resource';
import { ApiError } from '@/api/errors';
import { notify } from '@/lib/notify';
import { formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';

/**
 * Option sets and their options (06 §2) — gender, top/bottom/shoe sizes.
 *
 * Nested rather than two screens because an option is meaningless outside its
 * set: `boy` in `gender` and `boy` in `top_size` are different rows, and a flat
 * option list would ask the admin to hold that distinction in their head.
 *
 * ⚠️ `code` is immutable on both, and the edit forms do not offer it. Child
 * answers store the option id and wizard fields reference the set by code —
 * renaming either orphans data that is already recorded.
 */
export function OptionSetsPage(): React.JSX.Element {
  const { rows, isLoading, error, refetch, invalidate } = useAdminList<AdminOptionSet>(
    RESOURCES.optionSets,
  );
  const [expanded, setExpanded] = useState<string | null>(null);
  const [editingSet, setEditingSet] = useState<AdminOptionSet | 'new' | null>(null);

  return (
    <Page
      title="Option sets"
      description="Closed lists the wizard offers as select, radio or multi-select fields."
      breadcrumbs={[{ label: 'Master data' }, { label: 'Option sets' }]}
      action={
        <Button variant="primary" onClick={() => setEditingSet('new')}>
          <Plus aria-hidden="true" />
          Add option set
        </Button>
      }
    >
      <div className="space-y-5">
        <Notice title="Codes are permanent">
          A set&apos;s code is referenced by wizard fields and an option&apos;s code by stored child
          answers. Neither can be renamed after it is created.
        </Notice>

        {error ? (
          <div className="overflow-hidden rounded-card border border-line bg-surface">
            <ErrorState error={error} onRetry={refetch} endpoint="GET /admin/option-sets" />
          </div>
        ) : isLoading ? (
          <div className="overflow-hidden rounded-card border border-line bg-surface">
            <TableSkeleton columns={3} />
          </div>
        ) : !rows || rows.length === 0 ? (
          <div className="overflow-hidden rounded-card border border-line bg-surface">
            <EmptyState
              title="No option sets yet"
              description="Create one before adding a select or radio field to the wizard."
              action={
                <Button variant="primary" onClick={() => setEditingSet('new')}>
                  <Plus aria-hidden="true" />
                  Add option set
                </Button>
              }
            />
          </div>
        ) : (
          <div className="space-y-3">
            {rows.map((set) => (
              <OptionSetCard
                key={set.id}
                set={set}
                open={expanded === set.id}
                onToggle={() => setExpanded((prev) => (prev === set.id ? null : set.id))}
                onEdit={() => setEditingSet(set)}
                onChanged={invalidate}
              />
            ))}
          </div>
        )}
      </div>

      {editingSet ? (
        <OptionSetDialog
          set={editingSet === 'new' ? null : editingSet}
          onClose={() => setEditingSet(null)}
          onSaved={() => {
            setEditingSet(null);
            invalidate();
          }}
        />
      ) : null}
    </Page>
  );
}

// ---------------------------------------------------------------------------

function OptionSetCard({
  set,
  open,
  onToggle,
  onEdit,
  onChanged,
}: {
  set: AdminOptionSet;
  open: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onChanged: () => void;
}): React.JSX.Element {
  const reorder = useReorder(
    RESOURCES.optionSets,
    `${RESOURCES.optionSets}/${set.id}/options/reorder`,
  );
  const [editingOption, setEditingOption] = useState<AdminOption | 'new' | null>(null);
  const [deleting, setDeleting] = useState<AdminOption | null>(null);

  const { mutateAsync: update } = useUpdate();
  const { mutateAsync: remove } = useDelete();

  const activeCount = set.options.filter((option) => option.isActive).length;

  const toggleActive = useCallback(
    async (option: AdminOption) => {
      try {
        await update({
          resource: RESOURCES.options,
          id: option.id,
          values: { isActive: !option.isActive },
          successNotification: false,
          errorNotification: false,
        });
        notify.success(option.isActive ? 'Deactivated' : 'Activated', option.label);
      } catch (err) {
        notify.error(err, 'The change was not saved');
      } finally {
        onChanged();
      }
    },
    [update, onChanged],
  );

  return (
    <section className="overflow-hidden rounded-card border border-line bg-surface">
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className="flex min-w-0 flex-1 items-center gap-2.5 rounded text-left"
        >
          <ChevronRight
            aria-hidden="true"
            className={cn(
              'size-4 shrink-0 text-ink-faint transition-transform',
              open && 'rotate-90',
            )}
          />
          <span className="min-w-0">
            <span className="block truncate text-sm font-medium text-ink">{set.name}</span>
            {set.description ? (
              <span className="block truncate text-xs text-ink-soft">{set.description}</span>
            ) : null}
          </span>
        </button>

        <Mono>{set.code}</Mono>
        <span className="text-xs text-ink-soft" data-numeric>
          {formatNumber(activeCount)} active / {formatNumber(set.options.length)}
        </span>
        <Button size="sm" variant="ghost" onClick={onEdit}>
          Edit set
        </Button>
      </div>

      {open ? (
        <div className="border-t border-line">
          {set.options.length === 0 ? (
            <EmptyState
              title="No options in this set"
              description="A set with no options renders as an empty picker in the app."
              action={
                <Button variant="primary" onClick={() => setEditingOption('new')}>
                  <Plus aria-hidden="true" />
                  Add option
                </Button>
              }
            />
          ) : (
            <>
              <SortableList
                items={set.options}
                itemKey={(option) => option.id}
                onReorder={(ids) => void reorder(ids)}
                renderItem={(option) => (
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      className="min-w-0 flex-1 truncate rounded text-left text-sm text-ink"
                      onClick={() => setEditingOption(option)}
                    >
                      {option.label}
                    </button>
                    <Mono>{option.code}</Mono>
                    <span className="text-xs text-ink-soft" data-numeric>
                      {formatNumber(option.usageCount)} children
                    </span>
                    {option.isActive ? (
                      <Badge tone="success">Active</Badge>
                    ) : (
                      <Badge tone="neutral">Inactive</Badge>
                    )}
                    <Switch
                      checked={option.isActive}
                      onCheckedChange={() => void toggleActive(option)}
                      aria-label={`${option.isActive ? 'Deactivate' : 'Activate'} ${option.label}`}
                    />
                    <Tooltip
                      label={
                        option.usageCount > 0
                          ? 'In use by existing profiles — deactivate instead'
                          : 'Delete'
                      }
                    >
                      <span>
                        <Button
                          size="icon"
                          variant="ghost"
                          disabled={option.usageCount > 0}
                          aria-label={`Delete ${option.label}`}
                          onClick={() => setDeleting(option)}
                        >
                          <Trash2 aria-hidden="true" />
                        </Button>
                      </span>
                    </Tooltip>
                  </div>
                )}
              />
              <div className="border-t border-line px-4 py-3">
                <Button size="sm" variant="secondary" onClick={() => setEditingOption('new')}>
                  <Plus aria-hidden="true" />
                  Add option
                </Button>
              </div>
            </>
          )}
        </div>
      ) : null}

      {editingOption ? (
        <OptionDialog
          optionSetId={set.id}
          option={editingOption === 'new' ? null : editingOption}
          onClose={() => setEditingOption(null)}
          onSaved={() => {
            setEditingOption(null);
            onChanged();
          }}
        />
      ) : null}

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        title={`Delete ${deleting?.label ?? ''}?`}
        description="This removes the option from the set entirely. It cannot be undone."
        confirmLabel="Delete"
        destructive
        onConfirm={async () => {
          if (!deleting) return;
          try {
            await remove({
              resource: RESOURCES.options,
              id: deleting.id,
              successNotification: false,
              errorNotification: false,
            });
            notify.success('Deleted', deleting.label);
            setDeleting(null);
          } catch (err) {
            notify.error(err, 'The option was not deleted');
          } finally {
            onChanged();
          }
        }}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------

function OptionSetDialog({
  set,
  onClose,
  onSaved,
}: {
  set: AdminOptionSet | null;
  onClose: () => void;
  onSaved: () => void;
}): React.JSX.Element {
  const [code, setCode] = useState(set?.code ?? '');
  const [name, setName] = useState(set?.name ?? '');
  const [description, setDescription] = useState(set?.description ?? '');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [pending, setPending] = useState(false);

  const codeId = useId();
  const nameId = useId();
  const descriptionId = useId();

  const { mutateAsync: create } = useCreate();
  const { mutateAsync: update } = useUpdate();

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setPending(true);
    setFieldErrors({});
    try {
      if (set) {
        await update({
          resource: RESOURCES.optionSets,
          id: set.id,
          values: { name: name.trim(), description: description.trim() || undefined },
          successNotification: false,
          errorNotification: false,
        });
        notify.success('Saved', name.trim());
      } else {
        await create({
          resource: RESOURCES.optionSets,
          values: {
            code: code.trim(),
            name: name.trim(),
            description: description.trim() || undefined,
          },
          successNotification: false,
          errorNotification: false,
        });
        notify.success('Added', name.trim());
      }
      onSaved();
    } catch (err) {
      if (err instanceof ApiError) setFieldErrors(err.fieldErrors);
      notify.error(err, 'The option set was not saved');
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent title={set ? `Edit ${set.name}` : 'Add option set'}>
        <DialogForm
          onSubmit={(event) => {
            void submit(event);
          }}
          noValidate
        >
          <DialogBody>
            {set ? null : (
              <FormField
                label="Code"
                htmlFor={codeId}
                hint="Lowercase letters, digits and underscores. Permanent."
                error={fieldErrors['code']}
                required
              >
                <Input
                  value={code}
                  onChange={(event) =>
                    setCode(event.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))
                  }
                  className="font-mono"
                  autoFocus
                  required
                />
              </FormField>
            )}

            <FormField label="Name" htmlFor={nameId} error={fieldErrors['name']} required>
              <Input
                value={name}
                onChange={(event) => setName(event.target.value)}
                maxLength={60}
                required
              />
            </FormField>

            <FormField
              label="Description"
              htmlFor={descriptionId}
              error={fieldErrors['description']}
            >
              <Input
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                maxLength={255}
              />
            </FormField>
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} disabled={pending}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={pending} disabled={!name.trim()}>
              {set ? 'Save changes' : 'Add option set'}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}

function OptionDialog({
  optionSetId,
  option,
  onClose,
  onSaved,
}: {
  optionSetId: string;
  option: AdminOption | null;
  onClose: () => void;
  onSaved: () => void;
}): React.JSX.Element {
  const [code, setCode] = useState(option?.code ?? '');
  const [label, setLabel] = useState(option?.label ?? '');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [pending, setPending] = useState(false);

  const codeId = useId();
  const labelId = useId();

  const { mutateAsync: create } = useCreate();
  const { mutateAsync: update } = useUpdate();

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setPending(true);
    setFieldErrors({});
    try {
      if (option) {
        await update({
          resource: RESOURCES.options,
          id: option.id,
          values: { label: label.trim() },
          successNotification: false,
          errorNotification: false,
        });
        notify.success('Saved', label.trim());
      } else {
        await create({
          resource: RESOURCES.options,
          values: { optionSetId, code: code.trim(), label: label.trim() },
          successNotification: false,
          errorNotification: false,
        });
        notify.success('Added', label.trim());
      }
      onSaved();
    } catch (err) {
      if (err instanceof ApiError) setFieldErrors(err.fieldErrors);
      notify.error(err, 'The option was not saved');
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent title={option ? `Edit ${option.label}` : 'Add option'}>
        <DialogForm
          onSubmit={(event) => {
            void submit(event);
          }}
          noValidate
        >
          <DialogBody>
            {option ? null : (
              <FormField
                label="Code"
                htmlFor={codeId}
                hint="Unique within the set. Child answers store it, so it is permanent."
                error={fieldErrors['code']}
                required
              >
                <Input
                  value={code}
                  onChange={(event) =>
                    setCode(event.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))
                  }
                  className="font-mono"
                  autoFocus
                  required
                />
              </FormField>
            )}

            <FormField label="Label" htmlFor={labelId} error={fieldErrors['label']} required>
              <Input
                value={label}
                onChange={(event) => setLabel(event.target.value)}
                maxLength={60}
                required
              />
            </FormField>
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} disabled={pending}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={pending} disabled={!label.trim()}>
              {option ? 'Save changes' : 'Add option'}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}
